import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

import { ListCard } from "@/shared/components/ListCard";
import { Note, Row, SelectInput } from "@/shared/components/settings-atoms";
import { IconButton } from "@/shared/components/ui-kit";
import { useSpeechOptions, type SpeechModel } from "@/shared/api/speech";
import Section from "./Section";

import type { ConfigSection } from "../ConfigSectionHelp";
import type { Agent, VoiceSettings } from "@/shared/types";

const NONE: VoiceSettings = { voiceId: null, listenModel: null, speakModel: null };
const PICKER = "w-[300px] max-w-full";
const modelOption = (model: SpeechModel) => ({ id: model.id, label: model.about ? `${model.name} · ${model.about}` : model.name });

/* Calls: people call the agent from its chat in our apps, and it rings them with call_person.
   The voice it talks in (with a preview) and the models it hears and speaks with; each left at
   the server's default until picked. A call's answers come from the agent's own model. */
export default function VoiceSection({ agent, onUpdateAgent, onHelp }: {
  agent: Agent;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onHelp: (section: ConfigSection) => void;
}) {
  const enabled = !!agent.capabilities?.voiceEnabled;
  const options = useSpeechOptions();
  const voice = agent.voice ?? NONE;
  const defaults = options.data?.defaults;
  const toggle = () => onUpdateAgent((a) => ({ ...a, capabilities: { ...a.capabilities, voiceEnabled: !enabled } }));
  const set = (patch: Partial<VoiceSettings>) => onUpdateAgent((a) => ({ ...a, voice: { ...(a.voice ?? NONE), ...patch } }));
  // the default picked again is no pick: the agent follows the server's default
  const pick = (field: keyof VoiceSettings, fallback: string | undefined) => (value: string) => set({ [field]: value === fallback ? null : value });
  const voiceId = voice.voiceId ?? defaults?.voice ?? "";
  const voices = options.data?.voices ?? [];
  const chosen = voices.find((v) => v.id === voiceId);

  return (
    <Section section="voice" onHelp={onHelp} enabled={enabled} onToggle={toggle}>
      <ListCard>
        <Row className="px-4" title="Voice" desc={chosen?.description || "How it sounds on a call."}>
          {/* the picker and its sample share the column the rows below take */}
          <div className={`${PICKER} flex items-center gap-2`}>
            <div className="flex-1 min-w-0">
              <SelectInput
                value={voiceId}
                disabled={!voices.length}
                options={voices.length ? voices.map((v) => ({ id: v.id, label: v.name })) : [{ id: voiceId, label: options.isPending ? "Loading voices…" : "No voices" }]}
                onChange={pick("voiceId", defaults?.voice)}
              />
            </div>
            <Preview url={chosen?.preview_url ?? null} name={chosen?.name ?? "the voice"} />
          </div>
        </Row>
        <Row className="px-4" title="Speaks with" desc="Turns its words into speech.">
          <div className={PICKER}>
            <SelectInput
              value={voice.speakModel ?? defaults?.speak ?? ""}
              disabled={!options.data}
              options={(options.data?.speak ?? []).map(modelOption)}
              onChange={pick("speakModel", defaults?.speak)}
            />
          </div>
        </Row>
        <Row className="px-4" title="Hears with" desc="Turns what people say into text.">
          <div className={PICKER}>
            <SelectInput
              value={voice.listenModel ?? defaults?.listen ?? ""}
              disabled={!options.data}
              options={(options.data?.listen ?? []).map(modelOption)}
              onChange={pick("listenModel", defaults?.listen)}
            />
          </div>
        </Row>
      </ListCard>
      <Note>
        {options.data && !options.data.on && "Calls aren't set up on this server yet. "}
        People call it from its chat in the Definable app and on the web, and it rings them when
        something is worth a call. What it says comes from its model; each turn is in their chat.
        Publish to make calls use the change.
      </Note>
    </Section>
  );
}

/** Play the voice's sample; again to stop. */
function Preview({ url, name }: { url: string | null; name: string }) {
  const [playing, setPlaying] = useState(false);
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => audio.current?.pause(), []);
  useEffect(() => {
    audio.current?.pause();
    audio.current = null;
    setPlaying(false);
  }, [url]);
  const toggle = () => {
    if (!url) return;
    if (playing) {
      audio.current?.pause();
      setPlaying(false);
      return;
    }
    audio.current ??= new Audio(url);
    audio.current.onended = () => setPlaying(false);
    audio.current.currentTime = 0;
    void audio.current.play().then(() => setPlaying(true), () => setPlaying(false));
  };
  return (
    <IconButton onClick={toggle} disabled={!url} aria-label={playing ? `Stop ${name}'s sample` : `Hear ${name}`} title={playing ? "Stop" : "Hear it"}>
      {playing ? <Pause className="w-4 h-4" strokeWidth={1.75} /> : <Play className="w-4 h-4" strokeWidth={1.75} />}
    </IconButton>
  );
}
